import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import audiencesCRUDActions from '@mm/state/actions/AudiencesCRUDActions';
import AudiencesModel from '@mm/state/model/AudiencesModel';
import {
  hideSlideoutMenu,
  showSlideoutMenu,
} from '@mm/state/actions/viewActions';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';

export const useCreateNewAudience = (onCancel?: () => void) => {
  const router = useRouter();
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const dispatch: Dispatch<StoreState> = useDispatch();

  const createAudience = (
    entity?: Partial<AudiencesModel>,
    hiddenAudience?: boolean,
  ) => {
    const audience = entity
      ? entity
      : { filterParams: {}, deleteContacts: false, hidden: hiddenAudience };
    return dispatch(audiencesCRUDActions.create(audience, activeChatbotId));
  };

  const openAudienceSlideoutMenu = useCallback(
    (props: any) => {
      dispatch(showSlideoutMenu('newAudience', props));
    },
    [activeChatbotId],
  );

  const redirectToAudience = (audienceId: number) => {
    if (audienceId) {
      const path = BotBuilderRoutes.leadsContacts.getLink({
        chatbotId: activeChatbotId,
        audienceId,
      });
      router.history.push(path);
    }
  };

  const retrieveAudiences = () => {
    dispatch(audiencesCRUDActions.retrieveAll(activeChatbotId));
  };

  const hideAudienceSlideoutMenu = useCallback(() => {
    dispatch(hideSlideoutMenu());
  }, [activeChatbotId]);

  const createAudienceAndOpenMenu = async (
    {
      withRedirectOnClose,
      hiddenAudience,
    }: {
      withRedirectOnClose: boolean;
      hiddenAudience?: boolean;
    } = { withRedirectOnClose: false },
  ): Promise<number | undefined> => {
    const audience = await createAudience(undefined, hiddenAudience);

    if (audience) {
      const audienceId = audience.id;
      openAudienceSlideoutMenu({
        audienceId,
        onCancel,
        onClose: withRedirectOnClose
          ? () => {
              redirectToAudience(audienceId);
              retrieveAudiences();
            }
          : undefined,
      });
      return audienceId;
    }
  };

  return {
    createAudience,
    hideAudienceSlideoutMenu,
    createAudienceAndOpenMenu,
    redirectToAudience,
  };
};
